import React, { useState } from 'react';
import { 
  Upload, Download, Share2, QrCode, Lock, Shield, 
  Mail, Clock, Users, Smartphone, Play, ChevronRight,
  MousePointer, Eye, CheckCircle, Zap, FileArchive
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import SEOHead from '../components/SEOHead';

const helpSections = [
  {
    id: 'upload',
    icon: Upload,
    title: 'Envoyer des fichiers',
    description: 'Glissez-déposez vos fichiers ou cliquez pour les sélectionner',
    color: 'blue',
    steps: [
      { icon: MousePointer, text: 'Cliquez sur "Sélectionner des fichiers" ou glissez-déposez' },
      { icon: FileArchive, text: 'Vos fichiers sont automatiquement compressés en ZIP' },
      { icon: Share2, text: 'Obtenez un lien de partage instantané' }
    ],
    animation: 'upload'
  },
  {
    id: 'share',
    icon: Share2,
    title: 'Partager vos fichiers',
    description: 'Plusieurs méthodes pour partager avec vos destinataires',
    color: 'cyan',
    steps: [
      { icon: Link, text: 'Copiez le lien direct en un clic' },
      { icon: Mail, text: 'Envoyez par email directement depuis SENDBOX' },
      { icon: QrCode, text: 'Scannez le QR code avec un smartphone' }
    ],
    animation: 'share'
  },
  {
    id: 'security',
    icon: Shield,
    title: 'Sécuriser vos transferts',
    description: 'Protection maximale pour vos fichiers sensibles',
    color: 'green',
    steps: [
      { icon: Lock, text: 'Ajoutez un mot de passe pour protéger l\'accès' },
      { icon: Shield, text: 'Activez le chiffrement de bout en bout (E2E)' },
      { icon: Clock, text: 'Définissez une date d\'expiration automatique' }
    ],
    animation: 'security'
  },
  {
    id: 'download',
    icon: Download,
    title: 'Télécharger des fichiers',
    description: 'Récupérez les fichiers partagés avec vous',
    color: 'purple',
    steps: [
      { icon: Eye, text: 'Ouvrez le lien reçu par email ou QR code' },
      { icon: Lock, text: 'Saisissez le mot de passe si demandé' },
      { icon: Download, text: 'Cliquez sur Télécharger - c\'est tout !' }
    ],
    animation: 'download'
  },
  {
    id: 'pro',
    icon: Zap,
    title: 'Fonctionnalités Pro',
    description: 'Débloquez tout le potentiel de SENDBOX',
    color: 'yellow',
    steps: [
      { icon: Users, text: 'Notifications quand vos fichiers sont téléchargés' },
      { icon: Mail, text: 'Extensions Outlook et Gmail intégrées' },
      { icon: Smartphone, text: 'Application mobile PWA installable' }
    ],
    animation: 'pro'
  },
  {
    id: 'p2p',
    icon: Zap,
    title: 'Transfert P2P (Ultimate)',
    description: 'Envoyez directement de navigateur a navigateur',
    color: 'purple',
    steps: [
      { icon: Upload, text: 'Selectionnez vos fichiers et activez le mode P2P' },
      { icon: Share2, text: 'Partagez le lien ou QR code avec le destinataire' },
      { icon: Zap, text: 'Le fichier est transfere directement via WebRTC, sans passer par nos serveurs' },
      { icon: Download, text: 'Le destinataire telecharge le fichier instantanement' }
    ],
    animation: 'p2p'
  }
];

// Animated demo components
const UploadAnimation = () => (
  <div className="relative w-full h-48 bg-slate-900/50 rounded-xl overflow-hidden">
    {/* Drop zone */}
    <div className="absolute inset-4 border-2 border-dashed border-blue-500/50 rounded-lg flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-2 bg-blue-500/20 rounded-xl flex items-center justify-center animate-bounce">
          <Upload className="w-8 h-8 text-blue-400" />
        </div>
        <p className="text-blue-300/70 text-sm">Glissez vos fichiers ici</p>
      </div>
    </div>
    {/* Animated files dropping */}
    <div className="absolute top-0 left-1/4 animate-fall-1">
      <div className="w-8 h-10 bg-blue-500/30 rounded border border-blue-500/50"></div>
    </div>
    <div className="absolute top-0 left-1/2 animate-fall-2">
      <div className="w-8 h-10 bg-cyan-500/30 rounded border border-cyan-500/50"></div>
    </div>
    <div className="absolute top-0 right-1/4 animate-fall-3">
      <div className="w-8 h-10 bg-purple-500/30 rounded border border-purple-500/50"></div>
    </div>
    <style>{`
      @keyframes fall-1 { 0%, 100% { transform: translateY(-20px); opacity: 0; } 50% { transform: translateY(100px); opacity: 1; } }
      @keyframes fall-2 { 0%, 100% { transform: translateY(-20px); opacity: 0; } 60% { transform: translateY(100px); opacity: 1; } }
      @keyframes fall-3 { 0%, 100% { transform: translateY(-20px); opacity: 0; } 70% { transform: translateY(100px); opacity: 1; } }
      .animate-fall-1 { animation: fall-1 3s ease-in-out infinite; }
      .animate-fall-2 { animation: fall-2 3s ease-in-out infinite 0.3s; }
      .animate-fall-3 { animation: fall-3 3s ease-in-out infinite 0.6s; }
    `}</style>
  </div>
);

const ShareAnimation = () => (
  <div className="relative w-full h-48 bg-slate-900/50 rounded-xl overflow-hidden flex items-center justify-center">
    {/* Center file */}
    <div className="relative">
      <div className="w-16 h-16 bg-cyan-500/20 rounded-xl flex items-center justify-center border border-cyan-500/50 z-10 relative">
        <FileArchive className="w-8 h-8 text-cyan-400" />
      </div>
      {/* Animated rays */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute w-32 h-0.5 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent animate-pulse-ray-1 origin-left" style={{transform: 'rotate(0deg) translateX(30px)'}}></div>
        <div className="absolute w-32 h-0.5 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent animate-pulse-ray-2 origin-left" style={{transform: 'rotate(60deg) translateX(30px)'}}></div>
        <div className="absolute w-32 h-0.5 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent animate-pulse-ray-3 origin-left" style={{transform: 'rotate(120deg) translateX(30px)'}}></div>
        <div className="absolute w-32 h-0.5 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent animate-pulse-ray-1 origin-left" style={{transform: 'rotate(180deg) translateX(30px)'}}></div>
        <div className="absolute w-32 h-0.5 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent animate-pulse-ray-2 origin-left" style={{transform: 'rotate(240deg) translateX(30px)'}}></div>
        <div className="absolute w-32 h-0.5 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent animate-pulse-ray-3 origin-left" style={{transform: 'rotate(300deg) translateX(30px)'}}></div>
      </div>
      {/* Icons around */}
      <div className="absolute -top-12 left-1/2 -translate-x-1/2 animate-float-1"><Mail className="w-6 h-6 text-blue-400" /></div>
      <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 animate-float-2"><QrCode className="w-6 h-6 text-purple-400" /></div>
      <div className="absolute top-1/2 -left-14 -translate-y-1/2 animate-float-3"><Share2 className="w-6 h-6 text-cyan-400" /></div>
      <div className="absolute top-1/2 -right-14 -translate-y-1/2 animate-float-1"><Link className="w-6 h-6 text-green-400" /></div>
    </div>
    <style>{`
      @keyframes pulse-ray-1 { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.8; } }
      @keyframes pulse-ray-2 { 0%, 100% { opacity: 0.3; } 60% { opacity: 0.8; } }
      @keyframes pulse-ray-3 { 0%, 100% { opacity: 0.3; } 40% { opacity: 0.8; } }
      @keyframes float-icon { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
      .animate-pulse-ray-1 { animation: pulse-ray-1 2s ease-in-out infinite; }
      .animate-pulse-ray-2 { animation: pulse-ray-2 2s ease-in-out infinite 0.3s; }
      .animate-pulse-ray-3 { animation: pulse-ray-3 2s ease-in-out infinite 0.6s; }
      .animate-float-1 { animation: float-icon 3s ease-in-out infinite; }
      .animate-float-2 { animation: float-icon 3s ease-in-out infinite 0.5s; }
      .animate-float-3 { animation: float-icon 3s ease-in-out infinite 1s; }
    `}</style>
  </div>
);

const SecurityAnimation = () => (
  <div className="relative w-full h-48 bg-slate-900/50 rounded-xl overflow-hidden flex items-center justify-center">
    <div className="relative">
      {/* Shield with lock */}
      <div className="relative">
        <Shield className="w-20 h-20 text-green-500/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Lock className="w-8 h-8 text-green-400 animate-pulse" />
        </div>
      </div>
      {/* Rotating ring */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-28 h-28 border-2 border-dashed border-green-500/30 rounded-full animate-spin-slow"></div>
      </div>
      {/* Particles */}
      <div className="absolute -top-4 -left-4 w-2 h-2 bg-green-400 rounded-full animate-particle-1"></div>
      <div className="absolute -top-4 -right-4 w-2 h-2 bg-cyan-400 rounded-full animate-particle-2"></div>
      <div className="absolute -bottom-4 -left-4 w-2 h-2 bg-blue-400 rounded-full animate-particle-3"></div>
      <div className="absolute -bottom-4 -right-4 w-2 h-2 bg-purple-400 rounded-full animate-particle-4"></div>
    </div>
    <style>{`
      @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes particle { 0%, 100% { opacity: 0; transform: scale(0); } 50% { opacity: 1; transform: scale(1); } }
      .animate-spin-slow { animation: spin-slow 10s linear infinite; }
      .animate-particle-1 { animation: particle 2s ease-in-out infinite; }
      .animate-particle-2 { animation: particle 2s ease-in-out infinite 0.5s; }
      .animate-particle-3 { animation: particle 2s ease-in-out infinite 1s; }
      .animate-particle-4 { animation: particle 2s ease-in-out infinite 1.5s; }
    `}</style>
  </div>
);

const DownloadAnimation = () => (
  <div className="relative w-full h-48 bg-slate-900/50 rounded-xl overflow-hidden flex items-center justify-center">
    <div className="relative">
      {/* Download arrow */}
      <div className="w-16 h-16 bg-purple-500/20 rounded-xl flex items-center justify-center border border-purple-500/50">
        <Download className="w-8 h-8 text-purple-400 animate-bounce-download" />
      </div>
      {/* Progress bar */}
      <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-32">
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full animate-progress"></div>
        </div>
        <p className="text-center text-xs text-purple-300/70 mt-1">Téléchargement...</p>
      </div>
    </div>
    <style>{`
      @keyframes bounce-download { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(5px); } }
      @keyframes progress { 0% { width: 0%; } 100% { width: 100%; } }
      .animate-bounce-download { animation: bounce-download 1s ease-in-out infinite; }
      .animate-progress { animation: progress 2s ease-in-out infinite; }
    `}</style>
  </div>
);

const ProAnimation = () => (
  <div className="relative w-full h-48 bg-slate-900/50 rounded-xl overflow-hidden flex items-center justify-center">
    <div className="relative">
      {/* Pro badge */}
      <div className="w-20 h-20 bg-gradient-to-br from-yellow-500/30 to-orange-500/30 rounded-2xl flex items-center justify-center border border-yellow-500/50 animate-glow">
        <Zap className="w-10 h-10 text-yellow-400" />
      </div>
      {/* Orbiting features */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute animate-orbit-1" style={{transformOrigin: '50px 50px'}}>
          <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center -translate-x-12">
            <Mail className="w-4 h-4 text-blue-400" />
          </div>
        </div>
        <div className="absolute animate-orbit-2" style={{transformOrigin: '50px 50px'}}>
          <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center -translate-x-12">
            <Users className="w-4 h-4 text-green-400" />
          </div>
        </div>
        <div className="absolute animate-orbit-3" style={{transformOrigin: '50px 50px'}}>
          <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center -translate-x-12">
            <Smartphone className="w-4 h-4 text-purple-400" />
          </div>
        </div>
      </div>
    </div>
    <style>{`
      @keyframes glow { 0%, 100% { box-shadow: 0 0 20px rgba(234, 179, 8, 0.3); } 50% { box-shadow: 0 0 40px rgba(234, 179, 8, 0.5); } }
      @keyframes orbit { from { transform: rotate(0deg) translateX(50px) rotate(0deg); } to { transform: rotate(360deg) translateX(50px) rotate(-360deg); } }
      .animate-glow { animation: glow 2s ease-in-out infinite; }
      .animate-orbit-1 { animation: orbit 6s linear infinite; }
      .animate-orbit-2 { animation: orbit 6s linear infinite 2s; }
      .animate-orbit-3 { animation: orbit 6s linear infinite 4s; }
    `}</style>
  </div>
);

const P2PAnimation = () => (
  <div className="relative w-full h-48 bg-slate-900/50 rounded-xl overflow-hidden flex items-center justify-center">
    <div className="flex items-center gap-12">
      {/* Sender */}
      <div className="text-center">
        <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center border border-purple-500/50 mx-auto mb-2">
          <Upload className="w-6 h-6 text-purple-400" />
        </div>
        <p className="text-xs text-purple-300/70">Expediteur</p>
      </div>
      {/* Animated connection line */}
      <div className="relative w-32">
        <div className="h-0.5 bg-purple-500/30 w-full"></div>
        <div className="absolute top-1/2 -translate-y-1/2 animate-p2p-packet-1">
          <div className="w-3 h-3 bg-purple-400 rounded-full shadow-lg shadow-purple-500/50"></div>
        </div>
        <div className="absolute top-1/2 -translate-y-1/2 animate-p2p-packet-2">
          <div className="w-2 h-2 bg-cyan-400 rounded-full shadow-lg shadow-cyan-500/50"></div>
        </div>
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 -top-4">
          <Zap className="w-5 h-5 text-purple-400 animate-pulse" />
        </div>
        <p className="text-[10px] text-purple-300/50 text-center mt-2">WebRTC direct</p>
      </div>
      {/* Receiver */}
      <div className="text-center">
        <div className="w-14 h-14 bg-cyan-500/20 rounded-xl flex items-center justify-center border border-cyan-500/50 mx-auto mb-2">
          <Download className="w-6 h-6 text-cyan-400" />
        </div>
        <p className="text-xs text-cyan-300/70">Destinataire</p>
      </div>
    </div>
    <style>{`
      @keyframes p2p-packet { 0% { left: 0; opacity: 0; } 20% { opacity: 1; } 80% { opacity: 1; } 100% { left: calc(100% - 12px); opacity: 0; } }
      .animate-p2p-packet-1 { animation: p2p-packet 1.5s ease-in-out infinite; }
      .animate-p2p-packet-2 { animation: p2p-packet 1.5s ease-in-out infinite 0.5s; }
    `}</style>
  </div>
);

const AnimationComponent = ({ type }) => {
  switch (type) {
    case 'upload': return <UploadAnimation />;
    case 'share': return <ShareAnimation />;
    case 'security': return <SecurityAnimation />;
    case 'download': return <DownloadAnimation />;
    case 'pro': return <ProAnimation />;
    case 'p2p': return <P2PAnimation />;
    default: return null;
  }
};

const HelpPage = () => {
  const [activeSection, setActiveSection] = useState('upload');

  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <SEOHead 
        title="Aide en ligne" 
        description="Consultez notre aide en ligne pour apprendre à utiliser SENDBOX : envoi de fichiers, partage par lien ou QR code, options de sécurité et plus." 
      />
      <NavigationMenu />
      
      <main className="pt-24 pb-32">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm mb-6">
            <Play className="w-4 h-4" />
            Guide interactif
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Aide en ligne
          </h1>
          <p className="text-lg text-blue-200/70 max-w-2xl mx-auto">
            Découvrez comment utiliser SENDBOX avec nos tutoriels animés. 
            Cliquez sur une section pour voir la démonstration.
          </p>
        </section>

        {/* Interactive Help Sections */}
        <section className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left: Section list */}
            <div className="space-y-3">
              {helpSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isActive 
                        ? `${colorClasses[section.color]} border-2` 
                        : 'bg-slate-800/30 border-blue-500/10 hover:border-blue-500/30'
                    }`}
                    data-testid={`help-section-${section.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isActive ? colorClasses[section.color] : 'bg-slate-700/50'}`}>
                        <Icon className={`w-5 h-5 ${isActive ? '' : 'text-blue-300/70'}`} />
                      </div>
                      <div>
                        <h3 className={`font-semibold ${isActive ? 'text-white' : 'text-blue-100/80'}`}>
                          {section.title}
                        </h3>
                        <p className="text-sm text-blue-300/50">{section.description}</p>
                      </div>
                      <ChevronRight className={`w-5 h-5 ml-auto transition-transform ${isActive ? 'rotate-90 text-white' : 'text-blue-300/30'}`} />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Right: Animation + Steps */}
            <div className="lg:col-span-2">
              {helpSections.filter(s => s.id === activeSection).map((section) => (
                <div key={section.id} className="bg-slate-800/40 border border-blue-500/20 rounded-2xl p-6 animate-in fade-in duration-300">
                  <h2 className="text-2xl font-bold text-white mb-4">{section.title}</h2>
                  
                  {/* Animation */}
                  <div className="mb-6">
                    <AnimationComponent type={section.animation} />
                  </div>

                  {/* Steps */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-blue-300/70 uppercase tracking-wide">Étapes</h3>
                    {section.steps.map((step, idx) => {
                      const StepIcon = step.icon;
                      return (
                        <div 
                          key={idx}
                          className="flex items-center gap-4 p-3 bg-slate-900/50 rounded-xl border border-blue-500/10"
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                            <span className="text-blue-400 font-bold text-sm">{idx + 1}</span>
                          </div>
                          <StepIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
                          <p className="text-blue-100/80">{step.text}</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA */}
                  <div className="mt-6 pt-6 border-t border-blue-500/10 flex flex-wrap gap-3">
                    <Link to="/">
                      <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
                        <Upload className="w-4 h-4 mr-2" />
                        Essayer maintenant
                      </Button>
                    </Link>
                    <Link to="/support">
                      <Button variant="outline" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10">
                        Contacter le support
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Quick Links */}
        <section className="max-w-4xl mx-auto px-6 mt-16">
          <div className="bg-slate-800/30 border border-blue-500/10 rounded-2xl p-6 text-center">
            <h2 className="text-xl font-bold text-white mb-2">Besoin de plus d'aide ?</h2>
            <p className="text-blue-300/60 mb-4">Consultez notre FAQ ou contactez notre équipe</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/page/faq">
                <Button variant="outline" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10">
                  Voir la FAQ
                </Button>
              </Link>
              <Link to="/support">
                <Button variant="outline" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10">
                  Support technique
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <DynamicFooter fixed={true} />
    </div>
  );
};

export default HelpPage;
